'use client';

import { useState, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { Hotel, Menu, X, ChevronRight } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { Footer } from '@/shared/layout';
import { ThemeToggle } from '@/shared/ui';
import {
  LanguageSelector, useTranslation, LocaleLink,
  SUPPORTED_LOCALES, type SupportedLocale,
} from '@/core/i18n';
import { useLocaleStore } from '@/core/i18n/locale.store';
import { isSupportedLocale } from '@/core/i18n/i18n.config';
import { LocaleContext } from '@/core/i18n/locale.context';

const navLinks = [
  { href: '/features', navKey: 'features' },
  { href: '/pricing', navKey: 'pricing' },
  { href: '/hotel-compliance', navKey: 'compliance' },
  { href: '/blog', navKey: 'blog' },
  { href: '/case-studies', navKey: 'caseStudies' },
  { href: '/partner-program', navKey: 'partners' },
  { href: '/contact', navKey: 'contact' },
] as const;

/** Strip locale prefix from pathname for active-link matching */
function stripLocale(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0 && (SUPPORTED_LOCALES as readonly string[]).includes(parts[0])) {
    return '/' + parts.slice(1).join('/');
  }
  return pathname;
}

/** Extract locale from URL path (e.g. /ar/features → 'ar') */
function extractLocaleFromPath(pathname: string): SupportedLocale {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0 && isSupportedLocale(parts[0])) {
    return parts[0] as SupportedLocale;
  }
  return 'en';
}

export default function PublicLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // Synchronously set locale from URL before any t() calls
  const urlLocale = extractLocaleFromPath(pathname);
  useMemo(() => {
    useLocaleStore.setState({ locale: urlLocale });
  }, [urlLocale]);

  const { t } = useTranslation();
  const isCheckout = false;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const strippedPath = stripLocale(pathname);
  const isActiveLink = (href: string) => {
    return strippedPath === href || strippedPath.startsWith(`${href}/`);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local UI state on route change
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <LocaleContext.Provider value={urlLocale}>
    <div className="min-h-screen flex flex-col bg-hms-bg scroll-smooth">
      {/* ── Sticky Header ── */}
      <header
        className={cn(
          'fixed top-0 inset-x-0 z-50 h-16 flex items-center justify-between px-5 md:px-10 transition-all duration-300',
          'border-b backdrop-blur-xl',
          scrolled
            ? 'border-hms-border/60 bg-hms-sidebar/85 shadow-lg shadow-black/20'
            : 'border-transparent bg-hms-sidebar/50'
        )}
      >
        {/* Logo */}
        <LocaleLink id="public-logo-link" href="/" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-hms-gold to-hms-goldLt flex items-center justify-center shadow-md shadow-hms-gold/20 transition-shadow group-hover:shadow-hms-gold/40">
            <Hotel className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-semibold text-hms-text text-[15px] tracking-tight">
            Hotel<span className="text-hms-gold">Stack</span>
          </span>
        </LocaleLink>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 text-[13px]">
          {navLinks.map((link) => (
            <LocaleLink
              key={link.href}
              id={`public-nav-${link.href.replace('/', '')}`}
              href={link.href}
              className={cn(
                'px-3.5 py-2 rounded-lg transition-all duration-200',
                isActiveLink(link.href)
                  ? 'bg-hms-gold/10 text-hms-gold'
                  : 'text-hms-textSub hover:text-hms-text hover:bg-white/[0.04]',
              )}
            >
              {t(`nav.${link.navKey}`)}
            </LocaleLink>
          ))}
        </nav>

        {/* Desktop Auth Actions */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSelector id="public-language-selector" compact />
          <ThemeToggle id="public-theme-toggle" />
          <div className="w-px h-6 bg-hms-border/60" />
          <LocaleLink
            id="public-signin-link"
            href="/auth/login"
            className="text-[13px] font-medium text-hms-textSub hover:text-hms-text transition-colors px-4 py-2 rounded-lg hover:bg-white/[0.04]"
          >
            {t('nav.signIn')}
          </LocaleLink>
          <LocaleLink
            id="public-register-link"
            href="/register-hotel"
            className="text-[13px] font-semibold text-black bg-gradient-to-r from-hms-gold to-hms-goldLt hover:from-hms-goldLt hover:to-hms-gold transition-all duration-300 px-5 py-2 rounded-lg shadow-md shadow-hms-gold/25 hover:shadow-hms-gold/40"
          >
            {t('nav.registerHotel')}
          </LocaleLink>
        </div>

        {/* Mobile Actions */}
        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle />
          <button
            id="public-mobile-menu-btn"
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.06] transition-colors"
            aria-label={mobileMenuOpen ? t('common.closeMenu') : t('common.openMenu')}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* ── Mobile Slide-in Overlay ── */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden',
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Slide Panel */}
      <aside
        className={cn(
          'fixed top-0 right-0 z-50 h-full w-72 bg-hms-sidebar border-l border-hms-border shadow-2xl shadow-black/40 transition-transform duration-300 ease-out md:hidden flex flex-col',
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-hms-border">
          <span className="font-semibold text-hms-text text-[15px] tracking-tight">
            Hotel<span className="text-hms-gold">Stack</span>
          </span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <button
              id="public-mobile-menu-close"
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-hms-textSub hover:text-hms-text hover:bg-white/[0.06] transition-colors"
              aria-label="Close menu"
            >
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {navLinks.map((link) => (
            <LocaleLink
              key={link.href}
              id={`public-mobile-nav-${link.href.replace('/', '')}`}
              href={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className={cn(
                'flex items-center justify-between px-3.5 py-3 rounded-lg text-[14px] transition-colors group',
                isActiveLink(link.href)
                  ? 'bg-hms-gold/10 text-hms-gold'
                  : 'text-hms-textSub hover:text-hms-text hover:bg-white/[0.04]',
              )}
            >
              {t(`nav.${link.navKey}`)}
              <ChevronRight className="w-4 h-4 text-hms-textDim group-hover:text-hms-gold transition-colors" />
            </LocaleLink>
          ))}
        </nav>

        <div className="p-4 border-t border-hms-border space-y-2.5">
          <LanguageSelector id="public-mobile-language-selector" dropUp className="mb-2" />
          <LocaleLink
            id="public-mobile-signin-link"
            href="/auth/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full text-center text-[13px] font-medium text-hms-textSub hover:text-hms-text border border-hms-border hover:border-hms-borderLt transition-all px-4 py-2.5 rounded-lg"
          >
            {t('nav.signIn')}
          </LocaleLink>
          <LocaleLink
            id="public-mobile-register-link"
            href="/register-hotel"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full text-center text-[13px] font-semibold text-black bg-gradient-to-r from-hms-gold to-hms-goldLt hover:from-hms-goldLt hover:to-hms-gold transition-all duration-300 px-4 py-2.5 rounded-lg shadow-md shadow-hms-gold/25"
          >
            {t('nav.registerHotel')}
          </LocaleLink>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 pt-16">{children}</main>

      {/* ── Footer ── */}
      {!isCheckout && <Footer />}
    </div>
    </LocaleContext.Provider>
  );
}
